'use client';
import * as SubframeCore from '@subframe/core';

import styles from '../UpgradeTemplatesLandingPage.module.scss';

import {
  Alert,
  Avatar,
  Badge,
  Breadcrumbs,
  Button,
  InformationalHeader,
  InfoTooltip,
  Switch,
  Table,
} from 'subframe/index';
import Page from 'components/Page';
import { updateUpgradeTemplate, useListSubscriptions } from 'api/frontend';
import AnalyticsEventLogger from 'utils/AnalyticsEventLogger';
import { useSnackbar } from 'notistack';
import useUserAccountState from 'hooks/useUserAccountState';
import { ResolvedEntitlementFeaturesItem, UpgradeTemplate } from 'api/models';
import { ConsoleLoader } from 'components/Loader';
import { getHumanReadableDate } from 'utils/subscription_plan';
import BaseTable from 'components/design-system/Table/BaseTable';
import { SyntheticEvent, useEffect, useState } from 'react';
import { useDebounce } from 'react-use';
import { Actions, AtomicTooltip } from 'components/design-system';
import { moreTooltipContent } from 'components/design-system/AtomicTooltip';
import useAccountIdRoute from 'hooks/useAccountIdRoute';
import { RouterLink } from 'components/RouterLink';
import {
  getTooltipForUpgradeRequestedBy,
  showCancel,
  getUpgradeTemplateStatusBadge,
  UpgradeViewMode,
  showArchive,
} from '../helpers/UpgradesHelpers';
import { somethingWentWrong, toastAutoHideDuration } from 'constants/toasts';
import { LockingWrapper } from 'components/LockingWrapper';
import { UPGRADE_TEMPLATES } from 'src/data/upgrades';
import { CLUSTERS } from 'src/data/version_register_cp';
import { Searchbar } from 'components/design-system/Searchbar';
import { CommentTooltip } from 'components/design-system/CommentTooltip';
import MarkUpgradeTemplateAsEnvironmentUpgraded from '../components/MarkUpgradeTemplateAsEnvironmentUpgraded';
import MarkUpgradeTemplateAsApproved from '../components/MarkUpgradeTemplateAsApproved';
import RegenerateTemplateDialog from '../components/RegenerateTemplateDialog';
import { filterUpgradeTemplatesByResources } from 'utils/upgrades';
import {
  useListClustersInfinite,
  useListUpgradeTemplatesInfinite,
} from 'api/frontend-infinite';
import { clusterFlattenPages, flattenPages } from 'utils/arrays';
import OwnerSelector from '../components/OwnerSelector';
import UpgradesCancelDialog from '../helpers/CancelEntryDialog';
import { ChangeNameDialog } from '../helpers/ChangeNameDialog';
import MultiSelectDropdown from 'components/MultiSelectDropdown';
import { openUpgradeExportInNewWindow } from '../helpers/export_full';
import { useExampleData } from 'hooks/useExampleData';
import { ReloadButton } from 'components/ReloadButton';
import MarkUpgradeTemplateAsArchived from '../components/MarkUpgradeTemplateAsArchived';

function ClusterUpgradeTemplatesLandingPage() {
  const { logEvent } = AnalyticsEventLogger();
  const { enqueueSnackbar } = useSnackbar();
  const { account, currentOrganization } = useUserAccountState();
  const [showExampleData, setShowExampleData] = useExampleData();

  const clusterUpgradeTemplatesBasePath = useAccountIdRoute(
    '/orgs/:orgId/accounts/:accountId/upgrades/templates/clusters',
  );
  const clustersUpgradesBasePath = useAccountIdRoute(
    '/orgs/:orgId/accounts/:accountId/upgrades/plans/clusters',
  );
  const clustersBasePath = useAccountIdRoute(
    '/orgs/:orgId/accounts/:accountId/clusters',
  );

  const [selected, setSelected] = useState<string[]>([
    'completed',
    'in-progress',
    'pending',
    'outdated',
    'approved',
    'available',
    'update-in-progress',
    'regenerate-in-progress',
  ]);

  //Subscriptions
  const { data: Subscriptions } = useListSubscriptions(
    currentOrganization?.slug,
    {},
    {
      request: { headers: { Authorization: `Bearer ${account?.token}` } },
    },
  );

  // Clusters
  const { data: clustersList } = useListClustersInfinite(
    '',
    {},
    {
      request: { headers: { Authorization: `Bearer ${account?.token}` } },
    },
    {
      initialSize: 100,
      revalidateFirstPage: false,
    },
  );

  const clusters = clusterFlattenPages(clustersList || []) || [];

  const clusterNameMap = new Map(
    (showExampleData ? CLUSTERS : clusters || []).map((c) => [c.id, c.name]),
  );

  const realClusters = clusters
    ? clusters.filter((c) => c?.internal_k8s_ref !== 'example-cluster-ref')
    : [];

  useEffect(() => {
    if (clusters && sessionStorage.getItem('example-data') === null) {
      setShowExampleData(!realClusters.length);
    }
  }, [clusters]);

  // Upgrade Templates
  const { data: rawUpgradeTemplatesData, mutate } =
    useListUpgradeTemplatesInfinite(
      {},
      {
        request: {
          headers: { Authorization: `Bearer ${account.token}` },
        },
      },
    );
  const upgradeTemplatesData = flattenPages(rawUpgradeTemplatesData);
  const exampleTemplates =
    filterUpgradeTemplatesByResources(
      UPGRADE_TEMPLATES,
    ).clusterUpgradeTemplates;
  const upgradeTemplates = showExampleData
    ? exampleTemplates
    : upgradeTemplatesData
    ? filterUpgradeTemplatesByResources(
        upgradeTemplatesData,
      ).clusterUpgradeTemplates.sort((a, b) => {
        return a.created > b.created ? -1 : 1; // Latest requested Upgrade Template should appear on top
      })
    : [];
  const [searchTemplate, setSearchTemplate] = useState('');

  const [templatesToShow, setTemplatesToShow] = useState(upgradeTemplates);
  const handleOnChange = (
    event: SyntheticEvent<Element, Event>,
    newValue: string,
  ) => {
    setSearchTemplate(newValue);
  };
  useDebounce(
    () => {
      if (searchTemplate) {
        logEvent('search-upgrade-templates', { searchTemplate });
      }
    },
    1000,
    [searchTemplate],
  );
  useEffect(() => {
    let templateList = upgradeTemplates;
    if (searchTemplate !== '') {
      templateList = templateList.filter((item) =>
        item?.name?.toLowerCase().includes(searchTemplate.toLowerCase()),
      );
    }
    if (selected.length > 0) {
      templateList = templateList.filter((t) => {
        if (t.status === 'available' || t.status === 'in-progress') {
          return selected.includes('available');
        } else {
          return selected.includes(t.status);
        }
      });
    }
    setTemplatesToShow(templateList);
  }, [rawUpgradeTemplatesData, searchTemplate, showExampleData, selected]);

  const showCreateExampleTemplateToast = (
    event: React.MouseEvent<HTMLDivElement, MouseEvent>,
  ) => {
    const exampleTemplateCreateToast =
      'No new Upgrade Templates can be created from example data';
    event.stopPropagation();
    event.preventDefault();
    enqueueSnackbar(exampleTemplateCreateToast, {
      variant: 'warning',
      autoHideDuration: toastAutoHideDuration,
    });
  };

  // custom environments
  const customEnvironmentTemplates = upgradeTemplates.filter(
    (u) =>
      u.environment &&
      !['prod', 'staging', 'dev'].includes(u.environment?.toLowerCase()),
  );
  let otherEnvsTooltipData: { [key: string]: number } = {
    total: 0,
  };
  if (customEnvironmentTemplates) {
    customEnvironmentTemplates.map((template) => {
      otherEnvsTooltipData[template.environment] = otherEnvsTooltipData[
        template.environment
      ]
        ? otherEnvsTooltipData[template.environment] + 1
        : 1;

      otherEnvsTooltipData.total += 1;
    });
  }

  // by default show lock is true
  const showUpgradePlanLock = Subscriptions
    ? !Subscriptions?.total_entitlement?.features?.includes(
        'request_upgrade_plan',
      )
    : true;

  const showTemplateLock = Subscriptions
    ? !Subscriptions?.total_entitlement?.features?.includes(
        'request_upgrade_template',
      )
    : true;

  const [openEditNameDialog, setOpenEditNameDialog] = useState(false);
  const [openCancelDialog, setOpenCancelDialog] = useState(false);
  const [markAsApprovedDialog, setMarkAsApprovedDialog] = useState(false);
  const [regenerateTemplateDialog, setRegenerateTemplateDialog] =
    useState(false);
  const [
    openMarkEnvironmentUpgradedDialog,
    setOpenMarkEnvironmentUpgradedDialog,
  ] = useState(false);
  const [
    openMarkUpgradeTemplateAsArchivedDialog,
    setOpenMarkUpgradeTemplateAsArchivedDialog,
  ] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<UpgradeTemplate>();

  return (
    <Page title="Cluster Upgrade Templates">
      <div
        style={{ marginLeft: '32px', width: 'calc(100% - 32px' }}
        className={styles['pageContents']}
      >
        <Breadcrumbs>
          <Breadcrumbs.Item>Upgrade Copilot</Breadcrumbs.Item>
          <Breadcrumbs.Divider />
          <Breadcrumbs.Item>Upgrade Templates</Breadcrumbs.Item>
          <Breadcrumbs.Divider />
          <Breadcrumbs.Item active={true}>Clusters</Breadcrumbs.Item>
        </Breadcrumbs>
        <div className={styles['header-c674606e']}>
          <div className="flex w-full items-center justify-between">
            <span className="text-section-header font-section-header text-default-font">
              Cluster Upgrade Templates
            </span>
            <div className="flex items-center gap-2">
              <ReloadButton reload={mutate} />
              {realClusters.length ? (
                <RouterLink to={`${clusterUpgradeTemplatesBasePath}/new`}>
                  <LockingWrapper
                    feature={
                      ResolvedEntitlementFeaturesItem.request_upgrade_template
                    }
                    showLock={showTemplateLock}
                    tooltipText={
                      showTemplateLock
                        ? undefined //default, handled in LockingWrapper
                        : 'Request Cluster Upgrade Template'
                    }
                  >
                    <Button
                      disabled={false}
                      size="large"
                      icon="FeatherPlus"
                      iconRight={showTemplateLock ? 'FeatherLock' : null}
                    >
                      Request Cluster Upgrade Template
                    </Button>
                  </LockingWrapper>
                </RouterLink>
              ) : (
                <LockingWrapper
                  feature={
                    ResolvedEntitlementFeaturesItem.request_upgrade_template
                  }
                  tooltipText="Add a cluster to get Upgrade Template"
                  showLock={showTemplateLock}
                >
                  <Button
                    size="large"
                    icon="FeatherPlus"
                    disabled={true}
                    iconRight={showTemplateLock ? 'FeatherLock' : null}
                  >
                    Request Cluster Upgrade Template
                  </Button>
                </LockingWrapper>
              )}
            </div>
          </div>
          <div className={styles['stack-0ba32313']}>
            <span className={styles['text-a9f8771b']}>
              Overview of all requested Cluster Upgrade Templates
            </span>
            <div className={styles['stack-6d118439']}>
              <span className={styles['text-2285b617']}>Show example data</span>
              <AtomicTooltip
                data-cy="show-example-data-tooltip"
                tooltipContent={
                  showExampleData
                    ? 'Show results from your onboarded clusters'
                    : 'Show example data'
                }
              >
                <Switch
                  data-cy="switch"
                  checked={showExampleData}
                  onCheckedChange={() => {
                    setShowExampleData(!showExampleData);
                    setSearchTemplate('');
                  }}
                />
              </AtomicTooltip>
            </div>
          </div>
        </div>
        {showExampleData && (
          <Alert
            variant="warning"
            title="You are currently viewing example data"
            description={`To view results from your onboarded clusters, toggle the "Show example data" button`}
          />
        )}
        {!showExampleData &&
        (clusters === undefined || upgradeTemplatesData === undefined) ? (
          <ConsoleLoader />
        ) : (
          <>
            {showExampleData ||
            realClusters.length ||
            upgradeTemplates.length ? (
              <div className={styles['stack-21aef114']}>
                <span className={styles['subheaderText']}>
                  Current Cluster Upgrade Templates
                </span>

                <>
                  {upgradeTemplates.length ? (
                    <>
                      <div className={styles['selectionHeader']}>
                        <InformationalHeader className="flex-[1_0_0] h-full w-full">
                          <InformationalHeader.Item
                            title="Total"
                            headingSlot={
                              <InfoTooltip tooltipText="Total Upgrade Templates created so far" />
                            }
                            bodySlot={
                              <span className="text-body-bold font-body-bold text-default-font">
                                {upgradeTemplates.length || 0}
                              </span>
                            }
                          />
                          {upgradeTemplates.filter(
                            (u) => u.environment?.toLowerCase() === 'prod',
                          ).length ? (
                            <InformationalHeader.Item
                              title="Prod"
                              headingSlot={
                                <InfoTooltip tooltipText="Upgrade Templates for Prod clusters" />
                              }
                              bodySlot={
                                <span className="text-body-bold font-body-bold text-default-font">
                                  {
                                    upgradeTemplates.filter(
                                      (u) =>
                                        u.environment?.toLowerCase() === 'prod',
                                    ).length
                                  }
                                </span>
                              }
                            />
                          ) : (
                            <></>
                          )}

                          {upgradeTemplates.filter(
                            (u) => u.environment?.toLowerCase() === 'staging',
                          ).length ? (
                            <InformationalHeader.Item
                              title="Staging"
                              headingSlot={
                                <InfoTooltip tooltipText="Upgrade Templates for Staging clusters" />
                              }
                              bodySlot={
                                <span className="text-body-bold font-body-bold text-default-font">
                                  {
                                    upgradeTemplates.filter(
                                      (u) =>
                                        u.environment?.toLowerCase() ===
                                        'staging',
                                    ).length
                                  }
                                </span>
                              }
                            />
                          ) : (
                            <></>
                          )}
                          {upgradeTemplates.filter(
                            (u) => u.environment?.toLowerCase() === 'dev',
                          ).length ? (
                            <InformationalHeader.Item
                              title="Dev/Test"
                              headingSlot={
                                <InfoTooltip tooltipText="Upgrade Templates for Dev/test clusters" />
                              }
                              bodySlot={
                                <span className="text-body-bold font-body-bold text-default-font">
                                  {
                                    upgradeTemplates.filter(
                                      (u) =>
                                        u.environment?.toLowerCase() === 'dev',
                                    ).length
                                  }
                                </span>
                              }
                            />
                          ) : (
                            <></>
                          )}
                          {customEnvironmentTemplates.length ? (
                            <InformationalHeader.Item
                              title="Other"
                              headingSlot={
                                <SubframeCore.HoverCard.Root>
                                  <SubframeCore.HoverCard.Trigger
                                    asChild={true}
                                  >
                                    <SubframeCore.Icon
                                      className={styles['icon-94f4ea2d']}
                                      name="FeatherInfo"
                                    />
                                  </SubframeCore.HoverCard.Trigger>
                                  <SubframeCore.HoverCard.Portal>
                                    <SubframeCore.HoverCard.Content
                                      side="bottom"
                                      align="center"
                                      sideOffset={4}
                                      asChild={true}
                                      style={{
                                        zIndex: 9999,
                                      }}
                                    >
                                      <div className="flex flex-col items-start gap-1 rounded bg-default-font pt-1 pr-1 pb-1 pl-1 shadow-large">
                                        <Table
                                          header={
                                            <Table.HeaderRow>
                                              <Table.HeaderCell>
                                                Environment
                                              </Table.HeaderCell>
                                              <Table.HeaderCell>
                                                Upgrade Templates
                                              </Table.HeaderCell>
                                            </Table.HeaderRow>
                                          }
                                        >
                                          {Object.keys(
                                            otherEnvsTooltipData,
                                          ).map((key, index) => {
                                            if (index < 6 && key !== 'total') {
                                              return (
                                                <Table.Row
                                                  key={key}
                                                  className="border-t border-solid border-neutral-700"
                                                >
                                                  <Table.Cell className="h-7 grow shrink-0 basis-0">
                                                    <span className="whitespace-nowrap text-body font-body text-black">
                                                      {key}
                                                    </span>
                                                  </Table.Cell>
                                                  <Table.Cell className="h-7 grow shrink-0 basis-0">
                                                    <span className="whitespace-nowrap text-body font-body text-black">
                                                      {
                                                        otherEnvsTooltipData[
                                                          key
                                                        ]
                                                      }
                                                    </span>
                                                  </Table.Cell>
                                                </Table.Row>
                                              );
                                            } else {
                                              return <></>;
                                            }
                                          })}

                                          {Object.keys(otherEnvsTooltipData)
                                            .length > 6 ? (
                                            <Table.Row className="border-t border-solid border-neutral-700">
                                              <Table.Cell className="h-7 grow shrink-0 basis-0">
                                                <span className="whitespace-nowrap text-body font-body text-black">
                                                  ...
                                                </span>
                                              </Table.Cell>
                                              <Table.Cell className="h-7 grow shrink-0 basis-0">
                                                <span className="whitespace-nowrap text-body font-body text-black">
                                                  ...
                                                </span>
                                              </Table.Cell>
                                            </Table.Row>
                                          ) : (
                                            <></>
                                          )}
                                          <Table.Row className="border-y border-solid border-neutral-700">
                                            <Table.Cell className="h-7 grow shrink-0 basis-0">
                                              <span className="whitespace-nowrap text-body-bold font-body-bold text-black">
                                                Total
                                              </span>
                                            </Table.Cell>
                                            <Table.Cell className="h-7 grow shrink-0 basis-0">
                                              <span className="whitespace-nowrap text-body-bold font-body-bold text-black">
                                                {otherEnvsTooltipData.total}
                                              </span>
                                            </Table.Cell>
                                          </Table.Row>
                                        </Table>
                                      </div>
                                    </SubframeCore.HoverCard.Content>
                                  </SubframeCore.HoverCard.Portal>
                                </SubframeCore.HoverCard.Root>
                              }
                              bodySlot={
                                <span className={styles['bodyBoldText']}>
                                  {otherEnvsTooltipData.total}
                                </span>
                              }
                            />
                          ) : (
                            <></>
                          )}
                        </InformationalHeader>

                        <MultiSelectDropdown
                          selectAdditionalClassNames="w-64"
                          selectTruncationLength={25}
                          emptyLabel="Status"
                          listHeader="Filter by Status"
                          options={[
                            { value: 'pending', label: 'Waiting for Template' },
                            { value: 'available', label: 'Available' },
                            { value: 'approved', label: 'Approved for Use' },
                            {
                              value: 'completed',
                              label: 'Environment Upgraded',
                            },
                            {
                              value: 'regenerate-in-progress',
                              label: 'Regenerating Template',
                            },
                            { value: 'cancelled', label: 'Canceled' },
                            { value: 'archived', label: 'Archived' },
                          ]}
                          selected={selected}
                          setSelected={setSelected}
                          iconName={
                            selected.length > 0 ? 'FeatherFilter' : null
                          }
                          variant={selected.length > 0 ? 'filled' : 'outline'}
                        />
                        <Searchbar
                          onChange={handleOnChange}
                          view={showExampleData ? 'example' : 'real'}
                        />
                      </div>
                      <div className={styles['stack-92c2e65f']}>
                        <BaseTable
                          settings={{
                            localStorageKey: 'clusters_upgrade_templates_table',
                            dataCyPrefix: 'upgrades-templates-table',
                          }}
                          data={templatesToShow}
                          noMatchingData={Boolean(
                            upgradeTemplates.length && !templatesToShow.length,
                          )}
                          rowLink={(template) =>
                            showExampleData &&
                            template.id !==
                              'upgtmpl_af7aff6e-aa2d-11ee-a506-0242ac191190' &&
                            template.id !==
                              'upgtmpl_4d283f64-45aa-4ccd-ad1d-218e90eb958e'
                              ? ''
                              : `${clusterUpgradeTemplatesBasePath}/${template.id}`
                          }
                          rowSettings={{
                            rowTooltip: (template) =>
                              template.status === 'pending'
                                ? getUpgradeTemplateStatusBadge(template.status)
                                    ?.tooltip || ''
                                : '',
                            preventClick: (template) =>
                              template.status === 'pending' ||
                              (showExampleData &&
                                template.id !==
                                  'upgtmpl_af7aff6e-aa2d-11ee-a506-0242ac191190' &&
                                template.id !==
                                  'upgtmpl_4d283f64-45aa-4ccd-ad1d-218e90eb958e'),
                            preventClickToastMessage: showExampleData
                              ? 'This is a demonstration view only at this point - only some Upgrade Templates are available'
                              : "This Upgrade Template is being generated. You'll be notified when it's published",
                          }}
                          columns={[
                            {
                              id: 'template',
                              title: 'Template',
                              cellStyle: {
                                width: '30%',
                                height: '110px',
                                flex: '0 0 auto',
                              },
                              cellType: 'cell',
                              render: (template) => {
                                const resources = (
                                  template.resources.clusters || []
                                ).map(
                                  (res) => clusterNameMap.get(res.id) || res.id,
                                );

                                return (
                                  <div className="flex items-start gap-2 ">
                                    <div className="flex flex-col items-start gap-2">
                                      <AtomicTooltip
                                        tooltipContent={template.name}
                                      >
                                        <div className="truncate-ellipsis ">
                                          <span
                                            className={styles['bodyBoldText']}
                                          >
                                            {template.name}
                                          </span>
                                        </div>
                                      </AtomicTooltip>
                                      <div className="flex items-start gap-2">
                                        {template.current_version ? (
                                          <span className="text-label font-label text-subtext-color">
                                            {`From: ${template.current_version}`}
                                          </span>
                                        ) : (
                                          <></>
                                        )}
                                        {template.desired_version ? (
                                          <span
                                            className={styles['text-c3480cc5']}
                                          >
                                            {`To: ${template.desired_version}`}
                                          </span>
                                        ) : (
                                          <></>
                                        )}
                                      </div>
                                      {resources.length > 0 ? (
                                        <div className="flex items-start gap-2">
                                          <span className="text-label font-label text-subtext-color">
                                            Representative Cluster:{' '}
                                            {resources.slice(0, 1).join(', ')}
                                          </span>
                                          {resources.length > 1 && (
                                            <AtomicTooltip
                                              tooltipContent={moreTooltipContent(
                                                resources,
                                                1,
                                              )}
                                            >
                                              <span className="text-body font-body text-subtext-color">
                                                +
                                                {Math.max(
                                                  0,
                                                  resources.length - 1,
                                                )}{' '}
                                                more
                                              </span>
                                            </AtomicTooltip>
                                          )}
                                        </div>
                                      ) : (
                                        <></>
                                      )}
                                    </div>
                                    {template.status === 'completed' &&
                                      template.status_comment && (
                                        <CommentTooltip
                                          userName={
                                            template?.completed_by?.name
                                          }
                                          picture={
                                            template?.completed_by?.picture
                                          }
                                          time={
                                            template?.updated
                                              ? template?.updated / 1000
                                              : undefined
                                          }
                                          comment={template?.status_comment}
                                        />
                                      )}
                                  </div>
                                );
                              },
                            },
                            {
                              id: 'status',
                              title: 'Status',
                              cellType: 'cell',
                              cellStyle: {
                                width: '10%',
                              },
                              render: (template) => {
                                return (
                                  <AtomicTooltip
                                    tooltipContent={
                                      getUpgradeTemplateStatusBadge(
                                        template.status,
                                      ).tooltip ||
                                      getUpgradeTemplateStatusBadge(
                                        template.status,
                                      ).title
                                    }
                                  >
                                    <Badge
                                      variant={
                                        getUpgradeTemplateStatusBadge(
                                          template.status,
                                        ).variant
                                      }
                                      style={{
                                        maxWidth: '150px',
                                        whiteSpace: 'nowrap',
                                        textOverflow: 'ellipsis',
                                        overflow: 'hidden',
                                      }}
                                    >
                                      {
                                        getUpgradeTemplateStatusBadge(
                                          template.status,
                                        ).title
                                      }
                                    </Badge>
                                  </AtomicTooltip>
                                );
                              },
                            },
                            {
                              id: 'environment',
                              title: 'Environment',
                              cellType: 'cell',
                              cellStyle: {
                                width: '10%',
                              },
                              render: (template) => {
                                return (
                                  <AtomicTooltip
                                    tooltipContent={template.environment}
                                  >
                                    <Badge
                                      variant="neutral"
                                      style={{
                                        maxWidth: '100px',
                                        whiteSpace: 'nowrap',
                                        textOverflow: 'ellipsis',
                                        overflow: 'hidden',
                                      }}
                                    >
                                      {template.environment}
                                    </Badge>
                                  </AtomicTooltip>
                                );
                              },
                            },
                            {
                              id: 'owner',
                              title: 'Owner',
                              cellType: 'cell',
                              cellStyle: {
                                width: '10%',
                              },
                              render: (template) => {
                                return (
                                  <>
                                    {template.owner?.id ? (
                                      <AtomicTooltip
                                        tooltipContent={template.owner?.name}
                                      >
                                        {template.owner?.picture ? (
                                          <Avatar
                                            size="small"
                                            image={template.owner?.picture}
                                            className="ph-no-capture"
                                          />
                                        ) : (
                                          <Avatar
                                            variant="neutral"
                                            size="small"
                                          >
                                            {template.owner?.name
                                              ?.charAt(0)
                                              ?.toUpperCase()}
                                          </Avatar>
                                        )}
                                      </AtomicTooltip>
                                    ) : (
                                      <></>
                                    )}
                                  </>
                                );
                              },
                            },
                            {
                              id: 'requested-by',
                              title: 'Requested by',
                              cellType: 'cell',
                              cellStyle: {
                                width: '10%',
                              },
                              render: (template) => {
                                return (
                                  <AtomicTooltip
                                    className="ph-no-capture"
                                    tooltipContent={getTooltipForUpgradeRequestedBy(
                                      template.requested_by?.name,
                                      template.created,
                                      UpgradeViewMode.Template,
                                    )}
                                  >
                                    {template.requested_by?.picture ? (
                                      <Avatar
                                        size="small"
                                        image={template.requested_by?.picture}
                                        className="ph-no-capture"
                                      />
                                    ) : (
                                      <Avatar variant="neutral" size="small">
                                        {template.requested_by?.name
                                          ?.charAt(0)
                                          ?.toUpperCase()}
                                      </Avatar>
                                    )}
                                  </AtomicTooltip>
                                );
                              },
                            },
                            {
                              id: 'updated',
                              title: 'Last Updated',
                              cellType: 'cell',
                              cellStyle: {
                                width: '10%',
                              },
                              render: (template) => {
                                return (
                                  <span className="text-label font-label text-subtext-color">
                                    {template.last_validated
                                      ? getHumanReadableDate(
                                          template.updated
                                            ? template.updated
                                            : template.last_validated,
                                        )
                                      : ''}
                                  </span>
                                );
                              },
                            },

                            {
                              id: 'approved',
                              title: 'Approved',
                              cellType: 'cell',
                              cellStyle: {
                                width: '15%',
                              },
                              render: (template) => {
                                return (
                                  <>
                                    {template.status === 'approved' &&
                                    template.approved ? (
                                      <div className="flex items-center gap-1">
                                        <span className="text-label font-label text-subtext-color">
                                          {getHumanReadableDate(
                                            template.approved,
                                          )}
                                        </span>
                                        <CommentTooltip
                                          className="ph-no-capture"
                                          userName={template?.approved_by?.name}
                                          picture={
                                            template?.approved_by?.picture
                                          }
                                          time={template.approved / 1000}
                                          comment={
                                            template?.status_comment || ''
                                          }
                                          action="approved this Upgrade Template"
                                        />
                                      </div>
                                    ) : (
                                      <></>
                                    )}
                                  </>
                                );
                              },
                            },
                          ]}
                          actions={[
                            {
                              id: 'actions',
                              render: (template) => {
                                const markdownExports = (
                                  template.exports || []
                                ).filter((e) => e.format === 'markdown');

                                return (
                                  <div className={styles['stack-f5e45396']}>
                                    <div
                                      className={styles['stack-f5e45396']}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        e.preventDefault();
                                      }}
                                      onContextMenu={(e) => {
                                        e.preventDefault();
                                      }}
                                    >
                                      <div className="pl-4">
                                        <Actions
                                          sections={[
                                            {
                                              title: 'Update Properties',
                                              items: [
                                                <Actions.Item
                                                  key="edit-name"
                                                  title="Edit Name"
                                                  icon="FeatherEdit2"
                                                  onClick={() => {
                                                    setSelectedTemplate(
                                                      template,
                                                    );
                                                    setOpenEditNameDialog(true);
                                                  }}
                                                  disabled={showExampleData}
                                                  tooltip={
                                                    showExampleData
                                                      ? 'Action is not available on example data'
                                                      : ''
                                                  }
                                                />,

                                                <OwnerSelector
                                                  key="change-owner"
                                                  onConfirm={async (
                                                    newOwner,
                                                  ) => {
                                                    if (showExampleData) {
                                                      enqueueSnackbar(
                                                        'Example templates cannot be updated',
                                                        {
                                                          variant: 'warning',
                                                          autoHideDuration:
                                                            toastAutoHideDuration,
                                                        },
                                                      );
                                                    } else {
                                                      try {
                                                        await updateUpgradeTemplate(
                                                          template.id,
                                                          {
                                                            owner: {
                                                              id: newOwner,
                                                            },
                                                          },
                                                          {
                                                            headers: {
                                                              Authorization: `Bearer ${account.token}`,
                                                            },
                                                          },
                                                        );
                                                        logEvent(
                                                          'upgrade-templates-clusters-owner-change',
                                                          {
                                                            template:
                                                              template.id,
                                                            ownerId: newOwner,
                                                          },
                                                        );
                                                        mutate();
                                                        enqueueSnackbar(
                                                          'Successfully updated the owner of the Upgrade Template',
                                                          {
                                                            variant: 'success',
                                                            autoHideDuration:
                                                              toastAutoHideDuration,
                                                          },
                                                        );
                                                      } catch (err) {
                                                        enqueueSnackbar(
                                                          somethingWentWrong.replace(
                                                            '<action>',
                                                            'updating the owner of the Upgrade Template',
                                                          ),
                                                          {
                                                            variant: 'error',
                                                            autoHideDuration:
                                                              toastAutoHideDuration,
                                                          },
                                                        );
                                                      }
                                                    }
                                                  }}
                                                />,
                                              ],
                                            },
                                            {
                                              title: 'Workflow Actions',
                                              items: [
                                                <Actions.Item
                                                  key="instantiate-plan"
                                                  title="Instantiate Plan"
                                                  icon="FeatherPlus"
                                                  link={`${clustersUpgradesBasePath}/new?template_id=${template.id}`}
                                                  disabled={
                                                    showExampleData ||
                                                    template.status !==
                                                      'approved'
                                                  }
                                                  tooltip={
                                                    showExampleData
                                                      ? 'Action is not available on example data'
                                                      : template.status !==
                                                        'approved'
                                                      ? 'Action is only available for Approved Upgrade Templates'
                                                      : showUpgradePlanLock
                                                      ? 'Action not available for your current subscription. Upgrade to Business plan.'
                                                      : ''
                                                  }
                                                  subscriptionLock={
                                                    showUpgradePlanLock
                                                  }
                                                />,
                                                <Actions.Item
                                                  key="preverify"
                                                  title="Preverify"
                                                  icon="FeatherShieldCheck"
                                                  disabled={true}
                                                  preview={true}
                                                />,
                                              ],
                                            },
                                            {
                                              title: 'Lifecycle Actions',
                                              items: [
                                                <Actions.Item
                                                  key="mark-approved"
                                                  title="Mark as Approved"
                                                  icon="FeatherVerified"
                                                  onClick={() => {
                                                    setSelectedTemplate(
                                                      template,
                                                    );
                                                    setMarkAsApprovedDialog(
                                                      true,
                                                    );
                                                  }}
                                                  disabled={
                                                    showExampleData ||
                                                    template.status !==
                                                      'in-progress'
                                                  }
                                                  tooltip={
                                                    showExampleData
                                                      ? 'Action is not available on example data'
                                                      : template.status !==
                                                        'in-progress'
                                                      ? 'Action is only available for Available Upgrade Templates'
                                                      : ''
                                                  }
                                                />,
                                                <Actions.Item
                                                  key="regenerate-template"
                                                  title="Regenerate Template"
                                                  icon="FeatherRefreshCcw"
                                                  onClick={() => {
                                                    setSelectedTemplate(
                                                      template,
                                                    );
                                                    setRegenerateTemplateDialog(
                                                      true,
                                                    );
                                                  }}
                                                  disabled={
                                                    showExampleData ||
                                                    !template.is_regenerate_pending ||
                                                    template.status !==
                                                      'in-progress'
                                                  }
                                                  tooltip={
                                                    showExampleData
                                                      ? 'Action is not available on example data'
                                                      : template.status !==
                                                        'in-progress'
                                                      ? 'Action is only available for Available Upgrade Templates'
                                                      : !template.is_regenerate_pending
                                                      ? 'No pending feedback for regeneration'
                                                      : ''
                                                  }
                                                />,
                                                <Actions.Item
                                                  key="mark-environment-upgraded"
                                                  title="Mark as Environment Upgraded"
                                                  icon="FeatherCheck"
                                                  onClick={() => {
                                                    setSelectedTemplate(
                                                      template,
                                                    );
                                                    setOpenMarkEnvironmentUpgradedDialog(
                                                      true,
                                                    );
                                                  }}
                                                  disabled={
                                                    showExampleData ||
                                                    template.status !==
                                                      'approved'
                                                  }
                                                  tooltip={
                                                    showExampleData
                                                      ? 'Action is not available on example data'
                                                      : template.status !==
                                                        'approved'
                                                      ? 'Action is only available for Approved Upgrade Templates'
                                                      : ''
                                                  }
                                                />,

                                                <Actions.Item
                                                  key="cancel-template"
                                                  title="Cancel Template"
                                                  icon="FeatherX"
                                                  onClick={() => {
                                                    setSelectedTemplate(
                                                      template,
                                                    );
                                                    setOpenCancelDialog(true);
                                                  }}
                                                  disabled={
                                                    showExampleData ||
                                                    !showCancel(template)
                                                  }
                                                  tooltip={
                                                    showExampleData
                                                      ? 'Example templates cannot be canceled'
                                                      : !showCancel(template)
                                                      ? 'Action is not available for this Upgrade Template'
                                                      : ''
                                                  }
                                                />,
                                                <Actions.Item
                                                  key="mark-archived"
                                                  title="Archive"
                                                  icon="FeatherArchive"
                                                  onClick={() => {
                                                    setSelectedTemplate(
                                                      template,
                                                    );
                                                    setOpenMarkUpgradeTemplateAsArchivedDialog(
                                                      true,
                                                    );
                                                  }}
                                                  disabled={
                                                    showExampleData ||
                                                    !showArchive(template)
                                                  }
                                                  tooltip={
                                                    showExampleData
                                                      ? 'Action is not available on example data'
                                                      : !showArchive(template)
                                                      ? 'Action is not available for this Upgrade Template'
                                                      : ''
                                                  }
                                                />,
                                              ],
                                            },
                                            {
                                              title: 'Export',
                                              items: [
                                                <Actions.Item
                                                  key="export-as-markdown"
                                                  title="Export as Markdown"
                                                  icon="FeatherCode2"
                                                  onClick={() => {
                                                    openUpgradeExportInNewWindow(
                                                      UpgradeViewMode.Template,
                                                      template.id,
                                                      account.token,
                                                      enqueueSnackbar,
                                                    );
                                                  }}
                                                  disabled={
                                                    showExampleData ||
                                                    markdownExports.length < 1
                                                  }
                                                  tooltip={
                                                    showExampleData
                                                      ? 'Example Upgrade Templates cannot be exported'
                                                      : markdownExports.length <
                                                        1
                                                      ? 'Action is not available for this Upgrade Template'
                                                      : ''
                                                  }
                                                />,
                                              ],
                                            },
                                          ]}
                                        />
                                      </div>
                                    </div>
                                    <SubframeCore.Icon
                                      className={
                                        template.status === 'pending'
                                          ? styles['icon-ef8c001b']
                                          : styles['bodyText']
                                      }
                                      name="FeatherChevronRight"
                                    />
                                  </div>
                                );
                              },
                            },
                          ]}
                        />
                        {selectedTemplate && (
                          <>
                            <ChangeNameDialog
                              key="edit-name"
                              open={openEditNameDialog}
                              setOpen={setOpenEditNameDialog}
                              mode={UpgradeViewMode.Template}
                              name={selectedTemplate?.name || ''}
                              onConfirm={async (newName) => {
                                if (showExampleData) {
                                  enqueueSnackbar(
                                    'Example templates cannot be updated',
                                    {
                                      variant: 'warning',
                                      autoHideDuration: toastAutoHideDuration,
                                    },
                                  );
                                } else {
                                  try {
                                    await updateUpgradeTemplate(
                                      selectedTemplate.id,
                                      { name: newName },
                                      {
                                        headers: {
                                          Authorization: `Bearer ${account.token}`,
                                        },
                                      },
                                    );
                                    logEvent(
                                      'upgrade-templates-clusters-name-change',
                                      {
                                        template: selectedTemplate.id,
                                        name: newName,
                                      },
                                    );
                                    mutate();
                                    enqueueSnackbar(
                                      'Successfully edited the requested properties of the Upgrade Template',
                                      {
                                        variant: 'success',
                                        autoHideDuration: toastAutoHideDuration,
                                      },
                                    );
                                  } catch (err) {
                                    enqueueSnackbar(
                                      somethingWentWrong.replace(
                                        '<action>',
                                        'updating requested properties of the Upgrade Template',
                                      ),
                                      {
                                        variant: 'error',
                                        autoHideDuration: toastAutoHideDuration,
                                      },
                                    );
                                  }
                                }
                              }}
                            />
                            <UpgradesCancelDialog
                              open={openCancelDialog}
                              setOpen={setOpenCancelDialog}
                              mode={UpgradeViewMode.Template}
                              onConfirm={async () => {
                                if (showExampleData) {
                                  enqueueSnackbar(
                                    'Example templates cannot be canceled',
                                    {
                                      variant: 'warning',
                                      autoHideDuration: toastAutoHideDuration,
                                    },
                                  );
                                } else {
                                  try {
                                    await updateUpgradeTemplate(
                                      selectedTemplate.id,
                                      { status: 'cancelled' },
                                      {
                                        headers: {
                                          Authorization: `Bearer ${account.token}`,
                                        },
                                      },
                                    );
                                    logEvent('upgrades-addons-plan-cancel', {
                                      templateId: selectedTemplate.id,
                                      name: selectedTemplate.name || '',
                                    });
                                    mutate();
                                    enqueueSnackbar(
                                      'Successfully canceled Upgrade Template',
                                      {
                                        variant: 'success',
                                        autoHideDuration: toastAutoHideDuration,
                                      },
                                    );
                                  } catch (err) {
                                    enqueueSnackbar(
                                      somethingWentWrong.replace(
                                        '<action>',
                                        'cancelling this Upgrade Template',
                                      ),
                                      {
                                        variant: 'error',
                                        autoHideDuration: toastAutoHideDuration,
                                      },
                                    );
                                  }
                                }
                              }}
                            />
                            <MarkUpgradeTemplateAsApproved
                              upgradeId={selectedTemplate.id}
                              mutateTemplate={mutate}
                              templateOwnerId={selectedTemplate.owner?.id || ''}
                              open={markAsApprovedDialog}
                              onClose={() => setMarkAsApprovedDialog(false)}
                            />
                            <RegenerateTemplateDialog
                              upgrade={selectedTemplate}
                              open={regenerateTemplateDialog}
                              onClose={() => setRegenerateTemplateDialog(false)}
                              onConfirm={mutate}
                              account={account}
                            />
                            <MarkUpgradeTemplateAsEnvironmentUpgraded
                              templateId={selectedTemplate.id}
                              mutateTemplate={mutate}
                              open={openMarkEnvironmentUpgradedDialog}
                              onClose={() =>
                                setOpenMarkEnvironmentUpgradedDialog(false)
                              }
                            />
                            <MarkUpgradeTemplateAsArchived
                              templateId={selectedTemplate.id}
                              mutateTemplate={mutate}
                              open={openMarkUpgradeTemplateAsArchivedDialog}
                              onClose={() =>
                                setOpenMarkUpgradeTemplateAsArchivedDialog(
                                  false,
                                )
                              }
                            />
                          </>
                        )}
                      </div>
                    </>
                  ) : (
                    <div className={styles['stack-4070173c']}>
                      <span className="text-body font-body text-subtext-color">
                        No Upgrade Templates requested.
                      </span>
                    </div>
                  )}
                </>
              </div>
            ) : (
              <div className={styles['stack-fd90eb5e']}>
                <span className={styles['bodyText']}>
                  No clusters onboarded so far. Click here to onboard your first
                  cluster
                </span>
                <RouterLink to={`${clustersBasePath}/new`}>
                  <Button
                    size="small"
                    icon="FeatherPlus"
                    onClick={() =>
                      logEvent('upgrades-templates-onboard-cluster-btn-click')
                    }
                  >
                    Add Cluster
                  </Button>
                </RouterLink>
              </div>
            )}
          </>
        )}
      </div>
    </Page>
  );
}

export default ClusterUpgradeTemplatesLandingPage;
